import {newSessionId} from './id-generators';
import {Storage} from '../platform-interfaces/storage';
import { syncSessionIdToPdtDb } from '../../platforms/browser/browser-session-validation';

interface SessionInfo {
  id: string;
  createdAt: number;
  lastUsed: number;
  isSynced: boolean;
  retry: number;
}

const SESSION_KEY = 'session_info';

async function updateSessionInfoInStorage(newSessionInfo: SessionInfo, storage: Storage) {
  await storage.setItem(SESSION_KEY, JSON.stringify(newSessionInfo))
}

async function getSessionInfoFromStorage(storage: Storage): Promise<SessionInfo | null> {
  const mayBeSessionInfo = await storage.getItem(SESSION_KEY);
  if (!mayBeSessionInfo) {
    return null
  }
  return JSON.parse(mayBeSessionInfo) as SessionInfo
}

const config = {
  inActiveThresholdInMins: 30,
  expiryInMins: 120,
} as const

function isSessionInfoValid(sessionInfo: SessionInfo | null): sessionInfo is SessionInfo {
  if (!sessionInfo) {
    return false
  }
  const now = Date.now();
  const isExpired = ((now - sessionInfo.createdAt) / 60_000) > config.expiryInMins;
  const isInActive = ((now - sessionInfo.lastUsed) / 60_000) > config.inActiveThresholdInMins;
  return !isExpired && !isInActive
}

async function createAndCacheSession(storage: Storage): Promise<SessionInfo> {
  const now = Date.now();
  const newSessionInfo: SessionInfo = {
    createdAt: now,
    lastUsed: now,
    id: newSessionId(),
    isSynced: false,
    retry: 0,
  }
  await updateSessionInfoInStorage(newSessionInfo, storage);
  setTimeout(async() =>{
    await syncSessionIdToPdtDb(true, undefined, storage)
  },0)
 
  return newSessionInfo
}

async function getCurrentSessionInfo(storage: Storage): Promise<SessionInfo> {
  const cachedSessionInfo = await getSessionInfoFromStorage(storage);
  if (isSessionInfoValid(cachedSessionInfo)) {
    return cachedSessionInfo
  }
  return createAndCacheSession(storage)
}


export class SessionManager {
  private readonly storage: Storage;

  constructor(storage: Storage) {
    this.storage = storage;
  }

  async getCurrentSessionId(): Promise<string> {
    const currentSessionInfo = await getCurrentSessionInfo(this.storage);
    return currentSessionInfo.id;
  }

  async updateLastUsed() {
    const currentSessionInfo = await getCurrentSessionInfo(this.storage);
    await updateSessionInfoInStorage({
      ...currentSessionInfo,
      lastUsed: Date.now()
    }, this.storage)
  }

  async lastUsed() {
    const currentSessionInfo = await getCurrentSessionInfo(this.storage);
    return currentSessionInfo.lastUsed;
  }

  async updateSyncStatus() {
    const currentSessionInfo = await getCurrentSessionInfo(this.storage);
    await updateSessionInfoInStorage({
      ...currentSessionInfo,
      isSynced: !currentSessionInfo?.isSynced
    }, this.storage)
  }

  async getSyncStatus() {
    const currentSessionInfo = await getCurrentSessionInfo(this.storage);
    return currentSessionInfo.isSynced;
  }
  async getRetryStatus() {
    const currentSessionInfo = await getCurrentSessionInfo(this.storage);
    return currentSessionInfo.retry;
  }
  async updateRetryStatus() {
    const currentSessionInfo = await getCurrentSessionInfo(this.storage);
    await updateSessionInfoInStorage({
      ...currentSessionInfo,
      retry: currentSessionInfo?.retry + 1
    }, this.storage)
  }

}
